/**
 * RepBuddy - Verifier Service
 *
 * NOTE: The primary verification pipeline has moved to the backend (server.js).
 * The backend's /api/hunter/search now returns pre-verified { contact, result } pairs,
 * so the frontend no longer needs to call runVerification() for Hunter results.
 *
 * This file is kept for standalone email verification use cases
 * (e.g., verifying a single email the user types directly).
 */

import { supabase } from '../lib/supabaseClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

// ─── Auth helper ──────────────────────────────────────────────────────────────

async function authedHeaders() {
  const base = { 'Content-Type': 'application/json' };
  if (!supabase) return base;

  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) return base;

  return { ...base, Authorization: `Bearer ${session.access_token}` };
}

// ─── Email pattern generator ──────────────────────────────────────────────────

export function generateEmailCandidates(firstName, lastName, domain) {
  const f  = (firstName || '').toLowerCase().replace(/[^a-z]/g, '');
  const l  = (lastName  || '').toLowerCase().replace(/[^a-z]/g, '');
  const fi = f[0] || '';
  const li = l[0] || '';

  if (!f && !l) return [];

  return [
    { email: `${f}.${l}@${domain}`,  pattern: 'first.last'  },
    { email: `${f}${l}@${domain}`,   pattern: 'firstlast'   },
    { email: `${fi}${l}@${domain}`,  pattern: 'flast'       },
    { email: `${f}${li}@${domain}`,  pattern: 'firstl'      },
    { email: `${f}@${domain}`,       pattern: 'first'       },
    { email: `${fi}.${l}@${domain}`, pattern: 'f.last'      },
    { email: `${l}.${f}@${domain}`,  pattern: 'last.first'  },
    { email: `${l}${fi}@${domain}`,  pattern: 'lastf'       },
  ].filter(c =>
    c.email &&
    !c.email.startsWith('.') &&
    !c.email.startsWith('@') &&
    !c.email.includes('..') &&
    c.email.includes('@')
  );
}

// ─── Backend: ZeroBounce verification ─────────────────────────────────────────

async function backendZeroBounce(email) {
  try {
    const r = await fetch(`${BACKEND_URL}/api/verify/zerobounce`, {
      method:  'POST',
      headers: await authedHeaders(),
      body:    JSON.stringify({ email }),
    });
    if (!r.ok) return null;
    const data = await r.json();
    if (data.source === 'zerobounce_no_key') return null;
    return data;
  } catch {
    return null;
  }
}

// ─── Backend: SMTP + MX verification ─────────────────────────────────────────

async function backendVerifyEmail(email) {
  try {
    const r = await fetch(`${BACKEND_URL}/api/verify`, {
      method:  'POST',
      headers: await authedHeaders(),
      body:    JSON.stringify({ email }),
    });
    if (!r.ok) return null;
    const d = await r.json();
    const smtpStep = d.steps?.find(s => s.id === 'smtp');
    const mxStep   = d.steps?.find(s => s.id === 'mx');
    return {
      verdict:    d.verdict || 'unknown',
      confidence: d.score   || 50,
      smtpValid:  smtpStep?.status === 'pass',
      mxValid:    mxStep?.status   === 'pass',
      mxRecord:   mxStep?.detail   || '',
    };
  } catch {
    return null;
  }
}

// ─── Chat-facing verification ─────────────────────────────────────────────────
// Used by the chat when the user asks "is x@y.com valid?". Unlike runVerification
// below, these surface the metering state: `usage` (live balance after the debit) and
// `blocked` (allowance exhausted / trial over), so the chat can show honest messages.

export async function verifyEmailInChat(email) {
  // One request: /api/verify is the single metered debit point AND merges the paid
  // provider (when configured) server-side, so verdict, score and steps always come from
  // one coherent source - never a provider verdict next to contradictory engine steps.
  let r;
  try {
    r = await fetch(`${BACKEND_URL}/api/verify`, {
      method:  'POST',
      headers: await authedHeaders(),
      body:    JSON.stringify({ email }),
    });
  } catch {
    return { error: 'Verification is temporarily unavailable - please try again.' };
  }

  if (r.status === 402) {
    const d = await r.json().catch(() => ({}));
    return { blocked: true, reason: d.error || 'out_of_verifications', usage: d.usage || null };
  }
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    return { error: d.error || 'Verification failed - please try again.' };
  }

  const d = await r.json();
  return {
    email:   d.email || email,
    verdict: d.verdict || 'unknown',
    score:   d.score ?? null,
    steps:   d.steps || [],
    usage:   d.usage || null,
  };
}

// Verify 2-20 inline emails in one go (the metered /api/verify/bulk endpoint).
export async function verifyEmailsBulk(emails) {
  let r;
  try {
    r = await fetch(`${BACKEND_URL}/api/verify/bulk`, {
      method:  'POST',
      headers: await authedHeaders(),
      body:    JSON.stringify({ emails }),
    });
  } catch {
    return { error: 'Verification is temporarily unavailable - please try again.' };
  }
  if (r.status === 402) {
    const d = await r.json().catch(() => ({}));
    return { blocked: true, reason: d.error || 'out_of_verifications', usage: d.usage || null };
  }
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    return { error: d.error || 'Verification failed - please try again.' };
  }
  return r.json(); // { total, results: [{email, verdict, smtpNote?|detail?}], usage }
}

// Verify a whole email list (CSV upload) via the streaming endpoint - up to 200 rows,
// 1 email-verification credit per address checked. onEvent receives each SSE event
// (start / progress / blocked / done). Resolves with the final done payload.
export async function verifyEmailsStream(emails, onEvent = () => {}) {
  const response = await fetch(`${BACKEND_URL}/api/verify/emails/stream`, {
    method:  'POST',
    headers: await authedHeaders(),
    body:    JSON.stringify({ emails }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Verification failed with status ${response.status}`);
  }

  const reader  = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let done_ = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      let event;
      try { event = JSON.parse(line.slice(6)); } catch { continue; }
      onEvent(event);
      if (event.type === 'done') done_ = event;
    }
  }
  if (!done_) throw new Error('Verification stream ended unexpectedly - please try again.');
  return done_; // { total, checked, results: [{email, verdict, score?, detail?}], usage }
}

// ─── Standalone verification (for single-email use cases) ─────────────────────

export async function runVerification({
  firstName   = '',
  lastName    = '',
  company     = '',
  domain      = '',
  email       = '',
  linkedinUrl = '',
  mode        = 'full',
}) {
  if (!email || !email.includes('@')) {
    return {
      email:              email || '',
      verdict:            'invalid',
      confidence:         0,
      smtpValid:          false,
      mxValid:            false,
      mxRecord:           '',
      emailVerified:      false,
      employmentVerified: false,
      currentCompany:     company || '',
      pattern:            '',
      source:             'No email to verify',
      alternates:         [],
    };
  }

  const zbResult   = await backendZeroBounce(email);
  const smtpResult = await backendVerifyEmail(email);

  if (zbResult) {
    return {
      email,
      verdict:            zbResult.verdict,
      confidence:         zbResult.verdict === 'valid' ? 92
                        : zbResult.verdict === 'risky' ? 50 : 15,
      smtpValid:          smtpResult?.smtpValid ?? false,
      mxValid:            zbResult.mxFound || smtpResult?.mxValid || false,
      mxRecord:           zbResult.mxRecord || smtpResult?.mxRecord || '',
      emailVerified:      zbResult.verdict === 'valid',
      employmentVerified: false,
      currentCompany:     company || '',
      pattern:            'Direct match',
      source:             'RepBuddy verification',
      zbStatus:           zbResult.status,
      zbSubStatus:        zbResult.subStatus,
      alternates:         [],
    };
  }

  if (smtpResult) {
    return {
      email,
      verdict:            smtpResult.verdict,
      confidence:         smtpResult.confidence,
      smtpValid:          smtpResult.smtpValid,
      mxValid:            smtpResult.mxValid,
      mxRecord:           smtpResult.mxRecord,
      emailVerified:      smtpResult.verdict === 'valid',
      employmentVerified: false,
      currentCompany:     company || '',
      pattern:            'Direct match',
      source:             'RepBuddy SMTP engine',
      alternates:         [],
    };
  }

  return {
    email,
    verdict:            'risky',
    confidence:         30,
    smtpValid:          false,
    mxValid:            false,
    mxRecord:           '',
    emailVerified:      false,
    employmentVerified: false,
    currentCompany:     company || '',
    pattern:            '',
    source:             'Unverified',
    alternates:         [],
  };
}
